import { LoadingButton } from '@mui/lab'
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material'
import { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { track, useInitiatePayloadPayment, useSubmitPayloadPayment } from '../../../../logic'
import { useOptimisticUpdateNotificationAndOrderNotification, useOptimisticUpdatePayloadMetaData } from '../../../../logic/optimisticUpdates'
import { useGetOrder, useGetPayloadMetaData } from '../../../../logic/useApiQuery'
import { NumericFormat } from 'react-number-format'
import { usePayloadProcessingState } from '../../../context/payload-processing-state/PayloadProcessingState'
import { getTransactionsTotal, getCashToCloseRemaining } from '../../../../utils/getTransactionsTotal'
import { ErrorDialog } from '../../dialogs/ErrorDialog'
import { ConfirmAnotherPaymentDialog } from '../../dialogs/ConfirmAnotherPaymentDialog'
import { PortalOrder } from '../../../../types'
import { useOrganization } from '../../../OrganizationProvider'

export function CashToCloseForm() {
  const { closingId, id } = useParams()
  const { search } = useLocation()
  const { data: orderData } = useGetOrder(closingId ?? '', search ?? '')
  const { CompanyDisplayName } = useOrganization()

  const { optimisticUpdateNotificationAndOrderNotification } = useOptimisticUpdateNotificationAndOrderNotification()
  const { optimisticUpdatePayloadMetaData } = useOptimisticUpdatePayloadMetaData()

  const { mutate: mutateInitiatePayloadPayment, data: initiatePayloadPaymentData } = useInitiatePayloadPayment(closingId ?? '', id ?? '', search ?? '', {
    useErrorBoundary: true,
    onSuccess: () => {
      setPaymentStatus('idle')
    }
  })
  const { mutate: mutateSubmitPayloadPayment } = useSubmitPayloadPayment(closingId ?? '', id ?? '', search ?? '', {
    onSuccess: (data) => {
      if (!id || !closingId) return
      optimisticUpdateNotificationAndOrderNotification({orderKey: closingId, notificationId: id, search, patch: { IsPaymentSubmitted: true } })
      optimisticUpdatePayloadMetaData({orderKey: closingId, notificationId: id, search, patch: { transactionId: data.transactionId, amount: data.amount, createdAt: data.createdAt, accountNumber: data.accountNumber, status: data.status }})
      setPayloadState('completed')
    },
    useErrorBoundary: true
  })

  const { data: transactions } = useGetPayloadMetaData(closingId ?? '', id ?? '', search ?? '')
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing'>('processing')
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false)
  const { payloadState, setPayloadState, setProcessingAmount } = usePayloadProcessingState()
  const payloadFormRef = useRef(null)
  const hiddenPlaidButtonRef = useRef<HTMLButtonElement>(null)

  const total = getTransactionsTotal(transactions)
  const cashToCloseAmount = (orderData?.Order as PortalOrder)?.CashToCloseDetails?.amount //Just saying this is a PortalOrder to satisfy TS for now as AdhocPortalOrder is not relevant here.
  const remaining = Math.max(0, getCashToCloseRemaining(cashToCloseAmount, transactions))
  const [payloadError, setPayloadError] = useState<any>(null)
  const [amount, setAmount] = useState<string | number>(remaining)

  // Recalculate remaining amount when total changes (e.g. after a successful payment adds a new transaction)
  useEffect(() => {
    setAmount(remaining)
  }, [remaining])

  const amountGreaterThanZero = Number(amount) > 0
  const amountLessThanMax = Number(amount) <= 500000
  const amountRef = useRef(amount)
  amountRef.current = amount

  const hasTransactions = (transactions?.length ?? 0) > 0;
  const [showConfirmAnotherPayment, setShowConfirmAnotherPayment] = useState(false)

  // Initialize Payload form after payment is initiated and token is available.
  // Uses the standard React "ignore flag" cleanup pattern because Payload's SDK
  // does not expose .off() or .destroy() for listener removal. When deps change
  // and this effect re-runs, the cleanup sets isCurrent=false so that stale
  // listeners from the previous render cycle become no-ops.
  useEffect(() => {
    let isCurrent = true
    const plaidTarget = hiddenPlaidButtonRef.current
    if (initiatePayloadPaymentData?.clientToken && payloadFormRef.current && plaidTarget && amountGreaterThanZero && amountLessThanMax && termsAccepted) {
      Payload(initiatePayloadPaymentData?.clientToken)

      const form = new Payload.Form({
        form: payloadFormRef.current,
        preventDefaultOnSubmit: true
      })

      form.plaid(plaidTarget)
      //The following events are grabbed from here, but this documentation does not include 'closed'.
      //https://docs.payload.com/ui/payloadjs/secure-input/options/

      form.on('success', function(event: any) {
        if (!isCurrent) return // Prevent stale closures from firing duplicate submissions
        mutateSubmitPayloadPayment({payloadTransactionId: event.transaction_id})
        mutateInitiatePayloadPayment(undefined)
      })

      form.on('processing', function() {
        if (!isCurrent) return
        setProcessingAmount(Number(amountRef.current))
        setPayloadState('processing')
      })

      form.on('closed', function() {
        if (!isCurrent) return
        setPaymentStatus('idle')
      })
      form.on('error', function(error: any) {
        if (!isCurrent) return
        setProcessingAmount(null)
        setPayloadState('idle') // Reset shared state so Complete button re-enables on failure
        setPayloadError(error)
      })
    }

    return () => { isCurrent = false }
  }, [initiatePayloadPaymentData?.clientToken, mutateSubmitPayloadPayment, mutateInitiatePayloadPayment, setPayloadState, setProcessingAmount, amountGreaterThanZero, amountLessThanMax, termsAccepted])

  useEffect(() => {
    mutateInitiatePayloadPayment(undefined)
  }, [mutateInitiatePayloadPayment])

  const handleCompletePaymentClicked = () => {
    if (amountGreaterThanZero && amountLessThanMax) {
      if (hasTransactions) {
        setShowConfirmAnotherPayment(true)
      } else {
        setPaymentStatus('processing')
        if (hiddenPlaidButtonRef.current) {
          hiddenPlaidButtonRef.current.click()
        }
      }
    }
  }

  const handleConfirmAnotherPayment = () => {
    setPaymentStatus('processing')
    setShowConfirmAnotherPayment(false)
    if (hiddenPlaidButtonRef.current) {
      hiddenPlaidButtonRef.current.click()
    }
  }

  const paymentButtonText = hasTransactions ? 'Complete another payment' : 'Complete a payment'
  const paymentButtonVariant: 'text' | 'contained' = hasTransactions ? 'text' : 'contained'

  if (payloadError) {
    track('error', 'payload form', {}, {
      message: JSON.stringify(payloadError),
      component: 'CashToCloseForm',
    })
    return (
      <ErrorDialog titleText='Payment could not be processed'>
        <>
          We couldn't complete your payment from this account at this time.  Because closing funds are time sensitive, please try a different account or contact {CompanyDisplayName} for alternative payment instructions.  Contact information is available in the{' '}
          <Link to={`/closing/${closingId}/closing-contacts${search}`}>contacts section</Link>.
        </>
      </ErrorDialog>
    )
  }

  return (
    <>
      <>
        <form id="payload" ref={payloadFormRef} pl-form="payment"
          style={{
            display: payloadState === 'processing' ? 'none' : 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
          <Box display="flex" flexDirection="column" gap={3} alignItems="center">
            <Box alignItems="center" display='flex' flexDirection='column'>
              <Box width={'150px'}>
                <Typography variant="subtitle2" gutterBottom>Amount</Typography>
                <NumericFormat
                  allowNegative={false}
                  allowLeadingZeros={false}
                  value={amount}
                  onValueChange={(values) => {
                    setAmount(values.value)
                  }}
                  thousandSeparator={true}
                  prefix="$"
                  pl-input="amount"
                  style={{
                    width: '100%',
                    minHeight: '40px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '8px',
                    fontSize: '1rem',
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  }}
                />
              </Box>
              {!amountGreaterThanZero && <Typography color='red' fontSize='15px'>Amount must be greater than $0</Typography>}
              {!amountLessThanMax && <Typography color='red' fontSize='15px'>Amount must be less than $500,000</Typography>}
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
              }
              label={
                <Typography fontSize='15px'>
                  By completing a payment you confirm that you have read, accepted and agreed to be bound by Payload's{' '}
                  <a
                    href='https://payload.com/terms'
                    target='_blank'
                    rel='noopener noreferrer'

                    style={{ color: '#1976d2', textDecoration: 'underline' }}
                  >Terms of Use</a>
                    .
                </Typography>
              }
              sx={{ marginX: 4 }}
            />

            <Box alignItems="center" display='flex' flexDirection='column'>
              {/* Also prevent starting a new payment while one is in-flight */}
              <LoadingButton
                disabled={!amountGreaterThanZero || !amountLessThanMax || !termsAccepted || payloadState === 'processing'}
                loading={paymentStatus === 'processing'}
                loadingPosition="start"
                startIcon={<span />}
                onClick={handleCompletePaymentClicked}
                variant={paymentButtonVariant}
                color="primary"
                type="button"
                sx={paymentStatus === 'processing' ? { pl: 4 } : {}}
              >
                {paymentButtonText}
              </LoadingButton>
            </Box>
          </Box>
          <button ref={hiddenPlaidButtonRef} type="button" style={{ display: 'none' }} />
        </form>
      </>
      <ConfirmAnotherPaymentDialog
        open={showConfirmAnotherPayment}
        onCancel={() => setShowConfirmAnotherPayment(false)}
        onConfirm={handleConfirmAnotherPayment}
        totalCashToClose={Number((orderData?.Order as PortalOrder)?.CashToCloseDetails?.amount) || 0}
        alreadySubmittedAmount={total}
        additionalAmount={Number(amount)}
      />
    </>
  )
}

// Add TypeScript declaration for Payload as a global variable
declare global {
  var Payload: any
}
